import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Calendar,
  Plus,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Clock,
  Calculator,
  Plane,
  Trash2
} from 'lucide-react';
import { BudgetingService, BudgetSummary, CategorySummary, SavingsGoalProgress } from '../../lib/budgetingService';
import { useAuth } from '../../contexts/AuthContext';

import { Database } from '../../types/supabase';

type BudgetTransaction = Database['public']['Tables']['budget_transactions']['Row'] & {
  budget_categories: {
    id: string;
    name: string;
    type: string;
    color: string;
    icon: string;
  } | null;
};

export function BudgetDashboard() {
  const { user } = useAuth();
  const [budgetSummary, setBudgetSummary] = useState<BudgetSummary | null>(null);
  const [categorySummaries, setCategorySummaries] = useState<CategorySummary[]>([]);
  const [savingsGoals, setSavingsGoals] = useState<SavingsGoalProgress[]>([]);
  const [transactions, setTransactions] = useState<BudgetTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    
    loadDashboardData();

    const onRefresh = () => loadDashboardData();
    window.addEventListener('budget:refresh', onRefresh);

    return () => {
      window.removeEventListener('budget:refresh', onRefresh);
    };
  }, [user?.id]);

  const loadDashboardData = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const [activeBudget, summary, goals, trans] = await Promise.all([
        BudgetingService.getActiveBudget(user.id),
        BudgetingService.getBudgetSummary(user.id),
        BudgetingService.getSavingsGoalProgress(user.id),
        BudgetingService.getBudgetTransactions(user.id)
      ]);

      const categories = activeBudget
        ? await BudgetingService.getCategorySummaries(user.id, activeBudget.id)
        : [];

      const validTransactions = (trans || []).filter(t => t && t.id) as BudgetTransaction[];
      
      setBudgetSummary(summary);
      setCategorySummaries(categories);
      setSavingsGoals(goals);
      setTransactions(validTransactions);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setBudgetSummary({
        totalIncome: 0,
        totalExpenses: 0,
        totalSavings: 0,
        netAmount: 0,
        savingsRate: 0
      });
      setCategorySummaries([]);
      setSavingsGoals([]);
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'income':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'expense':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <DollarSign className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'income':
        return 'text-green-600';
      case 'expense':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Get recent transactions sorted by date
  const recentTransactions = transactions
    .slice(0, 10)
    .sort((a, b) => new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime());

  const handleDeleteTransaction = async (transactionId: string) => {
    if (!confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingId(transactionId);
      await BudgetingService.deleteBudgetTransaction(transactionId);
      
      // Remove from local state
      setTransactions(prev => prev.filter(t => t.id !== transactionId));
      
      // Trigger refresh event for other components
      window.dispatchEvent(new CustomEvent('budget:refresh'));
      window.dispatchEvent(new CustomEvent('budget:toast', { 
        detail: { type: 'success', message: 'Transaction deleted successfully' } 
      }));
    } catch (error) {
      console.error('Error deleting transaction:', error);
      window.dispatchEvent(new CustomEvent('budget:toast', { 
        detail: { type: 'error', message: 'Failed to delete transaction' } 
      }));
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusColor = (percentage: number) => {
    if (percentage >= 100) return 'text-red-600';
    if (percentage >= 80) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStatusIcon = (percentage: number) => {
    if (percentage >= 100) return <AlertCircle className="w-4 h-4" />;
    if (percentage >= 80) return <Clock className="w-4 h-4" />;
    return <CheckCircle className="w-4 h-4" />;
  };

  if (isLoading) {
    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-lg shadow-sm border p-6 animate-pulse">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-gray-200 rounded-full mb-4" />
                <div className="h-5 bg-gray-200 rounded w-32 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-48 mb-4" />
                <div className="h-3 bg-gray-200 rounded w-24" />
              </div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-6 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-32 mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Main Content Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Budget Calculator Card */}
        <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-idecide-primary rounded-full flex items-center justify-center mb-4">
              <Calculator className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Budget Calculator</h3>
            <p className="text-gray-600 mb-4">Create and manage your monthly budget</p>
            <div className="text-sm text-gray-500">
              {budgetSummary ? formatCurrency(budgetSummary.totalIncome) : '$0.00'} Income
            </div>
          </div>
        </div>

        {/* Expense Estimator Card */}
        <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-idecide-primary rounded-full flex items-center justify-center mb-4">
              <Plane className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Expense Estimator</h3>
            <p className="text-gray-600 mb-4">Plan for trips, events, and purchases</p>
            <div className="text-sm text-gray-500">
              {budgetSummary ? formatCurrency(budgetSummary.totalExpenses) : '$0.00'} Expenses
            </div>
          </div>
        </div>

        {/* Savings Goals Card */}
        <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-idecide-primary rounded-full flex items-center justify-center mb-4">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Savings Goals</h3>
            <p className="text-gray-600 mb-4">Set and track your financial goals</p>
            <div className="text-sm text-gray-500">
              {savingsGoals.length} Active Goals
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('budget:open-modal', { detail: 'budget' }))}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-idecide-primary rounded-lg flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className="font-medium text-gray-900">Budget Calculator</div>
              <div className="text-sm text-gray-500">Create your monthly budget</div>
            </div>
          </button>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('budget:open-modal', { detail: 'savings' }))}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-idecide-primary rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className="font-medium text-gray-900">Savings Goals</div>
              <div className="text-sm text-gray-500">Set financial targets</div>
            </div>
          </button>
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Category Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Category Breakdown</h3>
            <p className="text-sm text-gray-600">Spending by category this month</p>
          </div>
          <div className="p-6">
            {categorySummaries.length > 0 ? (
              <div className="space-y-4">
                {categorySummaries.map((category, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: category.category?.color || '#6B7280' }}
                      />
                      <span className="text-sm font-medium text-gray-900">
                        {category.category?.name || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(category.actual)}
                        </p>
                        <p className="text-xs text-gray-500">
                          of {formatCurrency(category.planned)}
                        </p>
                      </div>
                      <div className={`flex items-center space-x-1 ${getStatusColor(category.percentage)}`}>
                        {getStatusIcon(category.percentage)}
                        <span className="text-sm font-medium">
                          {category.percentage.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No budget categories found</p>
                <p className="text-sm text-gray-400">Create a budget to start tracking your spending</p>
              </div>
            )}
          </div>
        </div>

        {/* Savings Goals */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Savings Goals</h3>
            <p className="text-sm text-gray-600">Track your progress towards financial goals</p>
          </div>
          <div className="p-6">
            {savingsGoals.length > 0 ? (
              <div className="space-y-4">
                {savingsGoals.slice(0, 3).map((goal, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{goal.goal.name}</h4>
                      <span className="text-sm text-gray-500">
                        {formatCurrency(goal.goal.current_amount)} / {formatCurrency(goal.goal.target_amount)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(goal.progress, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{goal.progress.toFixed(1)}% complete</span>
                      {goal.daysRemaining > 0 && (
                        <span>{goal.daysRemaining} days remaining</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No savings goals set</p>
                <p className="text-sm text-gray-400">Create a savings goal to start planning for the future</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      {showDetails && (
        <div className="mt-8 bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
              <p className="text-sm text-gray-600">Your latest financial activity</p>
            </div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {showDetails ? <EyeOff className="w-5 h-5 text-gray-600" /> : <Eye className="w-5 h-5 text-gray-600" />}
            </button>
          </div>
          <div className="p-6">
            {recentTransactions.length > 0 ? (
              <div className="space-y-3">
                {recentTransactions.map((transaction, index) => (
                  <div 
                    key={transaction.id || index} 
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${
                        transaction.type === 'income' ? 'bg-green-100' : 
                        transaction.type === 'expense' ? 'bg-red-100' : 
                        'bg-gray-100'
                      }`}>
                        {getTransactionIcon(transaction.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {transaction.description || `${transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)} Transaction`}
                        </p>
                        <div className="flex items-center space-x-3 mt-1">
                          {transaction.budget_categories && (
                            <span 
                              className="text-xs px-2 py-1 rounded-full text-white font-medium"
                              style={{ backgroundColor: transaction.budget_categories.color || '#6B7280' }}
                            >
                              {transaction.budget_categories.name}
                            </span>
                          )}
                          <span className="text-xs text-gray-500">
                            {formatDate(transaction.transaction_date)}
                          </span>
                          {transaction.payment_method && (
                            <span className="text-xs text-gray-500 capitalize">
                              • {transaction.payment_method.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="ml-4 flex items-center space-x-4">
                      <div className="text-right">
                        <p className={`text-lg font-semibold ${getTransactionColor(transaction.type)}`}>
                          {transaction.type === 'income' ? '+' : '-'}
                          {formatCurrency(Number(transaction.amount))}
                        </p>
                      </div>
                      <button
                        onClick={() => transaction.id && handleDeleteTransaction(transaction.id)}
                        disabled={deletingId === transaction.id}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete transaction"
                      >
                        {deletingId === transaction.id ? (
                          <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No recent transactions</p>
                <p className="text-sm text-gray-400 mt-1">Add income or expenses to start tracking your spending</p>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('budget:open-modal', { detail: 'income' }))}
                  className="mt-4 bg-idecide-primary text-white px-4 py-2 rounded-lg hover:bg-idecide-primary/90 transition-colors text-sm"
                >
                  Add Your First Transaction
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
